/**
 * The table — felt, rail, pile wells and the stock marker.
 *
 * This is a single full-design-resolution `Mesh` running one custom GLSL
 * surface shader. Everything the player sees under the cards is produced by
 * that one shader, which is the whole point: felt, rail, wells and every glow
 * are lit per-pixel by the *same* virtual light (`LIGHT` in `@/pixi/layout`),
 * so they can never disagree with each other or with the card drop shadows
 * that are derived from the same constant.
 *
 * How depth is faked, in order:
 *
 * 1. A procedurally generated, seamlessly tiling fabric texture carries a
 *    packed normal map (R = nx, G = ny) and a height/AO channel (B). The felt
 *    is shaded per-pixel against LIGHT, so the weave itself catches the key
 *    light and the warm pool where the light rakes across the table falls off
 *    naturally toward the corners.
 * 2. The rail is a signed-distance ring. Its cross-section is a cushion whose
 *    slope is turned straight into a normal, so it catches a crisp specular on
 *    the upper-left and rolls into shadow on the lower-right — with a single
 *    metallic pinstripe near its inner lip.
 * 3. Pile wells are rounded-box depressions in the same height field. Their
 *    inner wall on the light-facing side turns *away* from the light and goes
 *    dark, the far wall turns *into* it and lights up. That asymmetry is what
 *    reads as a real depression rather than a painted-on ring — and it is not
 *    art-directed, it just falls out of the same light.
 * 4. The rail also occludes the felt tucked under it: the inner shadow is
 *    strongest exactly where the rail sits between the felt and the light.
 * 5. The lamp is *visible*. LIGHT is a point above the table aimed at its
 *    centre, so its cone meets the felt in an ellipse — centred on the aim
 *    point, lying along the beam's ground projection, stretched by 1/cos of the
 *    incidence angle because the light rakes. That pool is applied mostly as a
 *    gain on the felt's own key response rather than as a wash, so the weave
 *    rides with it and the black point at the edges never moves. A whisper of
 *    haze along the same cone says where the light comes in.
 * 6. The felt answers back. `impact()` drops a fast radial crease into the
 *    felt's normal that travels outward and dies inside ~200ms, so a card
 *    landing is acknowledged by the surface it lands on rather than happening
 *    in mid-air above an inert table. Felt, not water: it is a moving crease
 *    catching the key light, never a change of colour.
 *
 * All lighting is done in linear space and encoded back to sRGB at the end,
 * with a one-LSB dither, because a big smooth gradient over a near-black plum
 * bands horribly otherwise.
 *
 * Runtime state (hover, resolve pulses, wild alert, parallax tilt, impact
 * ripples) is four small uniform arrays that gsap writes into. No per-frame
 * allocation, no per-frame geometry, one draw call. Both of the effects above
 * are options with a hard off: at zero their GLSL is never emitted, so a
 * disabled effect costs literally nothing rather than multiplying by zero.
 */

import {
  CanvasSource,
  Container,
  Graphics,
  Mesh,
  MeshGeometry,
  Rectangle,
  Shader,
  Texture,
  UniformGroup,
  type DestroyOptions,
  type UniformData,
} from 'pixi.js';
import { gsap } from 'gsap';

import { motion, palette } from '@/core/tokens';
import { PILE_COUNT } from '@/game/types';
// Shared rather than re-queried: one media query, one cache, and the same test
// seam (`setReducedMotionOverride`) already used by the foil and the card shader.
import { prefersReducedMotion } from '@/pixi/filters/foil';
import {
  CARD,
  DESIGN,
  LIGHT,
  PILES,
  playCentre,
  PILE_MAX_CARDS,
  STOCK,
  TABLE,
  WELL,
  pilePosition,
} from '@/pixi/layout';

/* ------------------------------------------------------------------------ *
 * Tuning. Everything here is in design units and derives from layout/tokens.
 * ------------------------------------------------------------------------ */

/** Fabric tile: 256 texels (power of two, so `repeat` + mipmaps are legal). */
const FABRIC_TILE = 256;
/** Texels per weave crossing. 256 / 8 = 32 crossings per tile. */
const FABRIC_CELL = 8;
/** Design units covered by one fabric tile → ~4 units per thread crossing. */
const FABRIC_WORLD = 128;
/**
 * How hard the baked weave gradient pushes the normal. Kept well below the
 * well-wall slope: when the weave normals are as strong as the recess walls,
 * the wells stop reading as depressions and start reading as texture.
 */
const FABRIC_NORMAL_STRENGTH = 0.95;
/** The rail reuses the same texture, stretched, as lacquer grain. */
const RAIL_GRAIN_WORLD = 430;

/** Reference distances for the inverse-square-ish falloff of each light. */
const KEY_REF = 1500;
const FILL_REF = 1500;

/**
 * The virtual eye. There is no camera in 2D, but a *fixed* view vector makes
 * every flat surface mirror the key light identically and the specular reads
 * as a flat wash. Placing the eye above the table centre gives the highlight
 * somewhere to actually be, and gives the rail cushion an asymmetry that
 * agrees with the light. Any other module doing specular should use this too.
 */
const EYE_Z = 1750;

/**
 * Felt material. The sheen is dim and *tinted toward the felt's own hue*: a
 * white specular on a near-black plum greys the whole middle of the table out,
 * which is exactly how a premium felt stops looking like felt.
 */
const FELT_GAIN = 2.6;

/**
 * How the aura's 0..1 streak tint reaches the felt.
 *
 * Two numbers rather than one because the felt has to take the run's *colour*
 * and its *level* through different doors — see the block in `buildFragment`
 * that uses them. The aura ceilings its output at 0.10, so at the top of the
 * ladder these are a 40% hue rotation and a 26% lift.
 */
const STREAK_ROTATE = 4;
const STREAK_GAIN = 2.6;
const FELT_SPEC = 0.085;
const FELT_SPEC_POW = 22;

/** Rail material — darker and glossier than the felt, but still late-night. */
const RAIL_BASE = mixHex(palette.void, palette.plum, 0.32);
const RAIL_GAIN = 1.7;
const RAIL_SPEC = 0.11;
const RAIL_SPEC_POW = 60;
/** Peak cross-section slope of the rail cushion. */
const RAIL_SLOPE = 0.8;
/** Radius of the rail's inner edge; a rounded rect offset inward is exact. */
let RAIL_INNER_RADIUS = Math.max(TABLE.radius - TABLE.railThickness, 2);

/**
 * The single metallic pinstripe, positioned across the rail cross-section.
 * It reads mostly as albedo: at this width a specular-only bead would be
 * sub-pixel and would crawl, so the line is a quiet goldDeep inlay that simply
 * gets brighter where the rail itself is lit.
 */
const PIN_AT = 0.845;
const PIN_WIDTH = 1.7;
const PIN_MIX = 0.22;
const PIN_SPEC = 0.22;
const PIN_SPEC_POW = 80;

/** How far the rail's shadow reaches onto the felt, and how dark it gets. */
const RAIL_SHADOW_REACH = 44;
const RAIL_SHADOW_STRENGTH = 0.6;

/** Pile wells: a card, plus just enough margin to read as a recess. */
const WELL_MARGIN = WELL.margin;
const WELL_HALF_X = CARD.width / 2 + WELL_MARGIN;
const WELL_HALF_Y = CARD.height / 2 + WELL_MARGIN;
const WELL_RADIUS = CARD.cornerRadius + 6;
/** Width of the well's inner wall, and the slope amplitude across it. */
const WELL_WALL = 17;
const WELL_DEPTH = 8;
/** A whisper of raised felt just outside the rim, which sells the dig. */
const WELL_LIP = 1.1;
const WELL_LIP_OFFSET = 3;
const WELL_LIP_FALL = 5;
/** Gold hairline: sits just inside the rim, and stays very quiet. */
const WELL_HAIR_OFFSET = 2;
const WELL_HAIR_WIDTH = 1.25;
const WELL_HAIR_ALPHA = 0.05;
/**
 * How far a well's glow bleeds out onto the surrounding felt.
 *
 * Shared with `@/pixi/layout`, which places the total badges a fixed number of
 * these constants clear of the well so the glow cannot wash into them.
 */
const WELL_BLEED = WELL.bleed;

/**
 * Emissive weights, in linear light. The felt sits around 0.02 linear, so
 * these are deliberately small numbers: a hover pool at 0.085 already lifts
 * the well to roughly three times the surrounding felt, which is the
 * difference between "warm pool" and "desk lamp". Rim beats pool everywhere —
 * edge-lit reads as light in a recess, an even fill reads as a lit panel.
 */
const HOVER_POOL = 0.085;
const HOVER_RIM = 0.13;
const WILD_POOL = 0.075;
const WILD_RIM = 0.15;
/** A resolve is a moment, not a state, so it may be loud — but edge-lit. */
const PULSE_POOL = 0.2;
const PULSE_RIM = 0.42;
/** Parallax: design units the well contents shift per unit of tilt. */
const WELL_PARALLAX = 5;
/** How much tilt leans the well floor's normal. */
const WELL_TILT_NORMAL = 0.09;

/**
 * Wrap period for the felt shader's clock, in seconds.
 *
 * Chosen as exactly 100 cycles of the slower lamp term (2*PI/0.62 = 10.134s).
 * The faster term (1.47 rad/s) does not divide evenly and jumps 0.58 rad at the
 * wrap — but it carries an amplitude of 0.009 on `feltGain`, so the worst-case
 * discontinuity is under 0.2 of an 8-bit level on the centre felt, well below
 * the shader's own dither. Wrapping matters far more than the seam: an
 * unwrapped clock loses float precision after a long session and the breathing
 * visibly quantises.
 */
const LAMP_WRAP = 1013.4;

/**
 * How far a pile well is stretched downward, in design units.
 *
 * A five-card fan runs `(PILE_MAX_CARDS - 1) * PILE_FAN` past the first card's
 * centre. Covering ~62% of that keeps the recess reading as a bed for the whole
 * pile without turning it into a trench on an empty table.
 */
let WELL_EXTEND_Y = 0;

/** The stock marker is the same recess, cut a little deeper. */
const STOCK_DEPTH = WELL_DEPTH * 1.25;
const STOCK_HAIR_ALPHA = WELL_HAIR_ALPHA * 1.4;

/** Motion, derived from the shared vocabulary in tokens. */
/**
 * Hover is feedback about where the pointer IS, so latency reads as the game
 * being slow rather than as a graceful fade. Fast in, slower out: the lift
 * should feel like it was already there, while the release can relax.
 */
const HOVER_IN = motion.ui * 0.3;
const HOVER_OUT = motion.ui * 1.3;
const WILD_STAGGER = 0.09;
const WILD_RISE = motion.ui * 1.75;
const WILD_BREATH = 0.9;

/** Outcome → wash colour. Straight from the token palette, no exceptions. */
const PULSE_COLOUR = {
  twentyOne: palette.mint,
  fiveCard: palette.gold,
  wild: palette.violetLight,
  bust: palette.rose,
} as const;

export type PulseKind = keyof typeof PULSE_COLOUR;

/* ------------------------------------------------------------------------ *
 * The lamp, resolved into table-plane geometry.
 *
 * LIGHT is a point above the table aimed at its centre. A cone from there meets
 * the felt in an ellipse whose centre is the aim point, whose major axis lies
 * along the beam's ground projection, and which is stretched by 1/cos of the
 * incidence angle — a raking light makes a longer pool. Everything the pool and
 * the shaft are drawn from falls out of these five numbers, so neither can ever
 * point somewhere the shading and the card shadows do not.
 * ------------------------------------------------------------------------ */

/**
 * What the lamp is aimed at.
 *
 * NOT the centre of the design rect, which is where this used to point. The
 * key already falls off toward the lamp at (620, -240), so the felt is
 * naturally brightest in the upper-left; aiming the pool at the geometric
 * centre made the two effects cancel almost exactly. Measured, empty felt at
 * (300, 200) and the pile-1 well at (967, 490) came out equal to four
 * significant figures — so the brightest patch of felt on the table was dead
 * space, and the darkest usable felt was under pile 3's fifth-card position,
 * which is exactly where a five-card bonus or a bust resolves.
 *
 * This is the play centroid instead: the stock at x=250 plus a pile block
 * centred on PILE_CENTRE_X, and low enough to sit in the middle of a grown fan
 * rather than at the first card.
 */
let AIM_X = 0;
let AIM_Y = 0;

let BEAM_DX = 0;
let BEAM_DY = 0;

/** Unit direction the beam travels across the table, and its perpendicular. */
let BEAM_AXIS_X = 0;
let BEAM_AXIS_Y = 0;
let BEAM_PERP_X = 0;
let BEAM_PERP_Y = 0;

/** Cosine of the angle between the beam and the table's normal. */
let BEAM_COS = 0;

/** Felt half-extents, inside the rail — the pool is sized against these. */
let FELT_HALF_X = 0;
let FELT_HALF_Y = 0;
let FELT_HALF_DIAGONAL = 0;

/**
 * Recompute everything above that depends on the pile arrangement.
 *
 * These are `let` rather than `const` because the design rect and the pile grid
 * change with the device's orientation — see `@/pixi/layout`. Every one of them
 * is read only while a table is being built (the lamp aim and the pool size are
 * baked into the fragment shader as literals), so recomputing here, at the top
 * of `createTable`, is enough: a rotation rebuilds the table, and the rebuilt
 * table sees the new numbers.
 */
function syncGeometry(): void {
  RAIL_INNER_RADIUS = Math.max(TABLE.radius - TABLE.railThickness, 2);
  WELL_EXTEND_Y = PILES.fan * (PILE_MAX_CARDS - 1) * 0.62;

  const aim = playCentre();
  AIM_X = (STOCK.x + aim.x * 3) / 4;
  AIM_Y = aim.y + PILES.fan * 1.6;

  BEAM_DX = AIM_X - LIGHT.x;
  BEAM_DY = AIM_Y - LIGHT.y;
  const run = Math.hypot(BEAM_DX, BEAM_DY) || 1;

  BEAM_AXIS_X = BEAM_DX / run;
  BEAM_AXIS_Y = BEAM_DY / run;
  BEAM_PERP_X = -BEAM_AXIS_Y;
  BEAM_PERP_Y = BEAM_AXIS_X;

  BEAM_COS = Math.max(LIGHT.z / Math.hypot(BEAM_DX, BEAM_DY, LIGHT.z), 0.05);

  FELT_HALF_X = DESIGN.width / 2 - TABLE.inset - TABLE.railThickness;
  FELT_HALF_Y = DESIGN.height / 2 - TABLE.inset - TABLE.railThickness;
  FELT_HALF_DIAGONAL = Math.hypot(FELT_HALF_X, FELT_HALF_Y);
}

syncGeometry();

/* ------------------------------------------------------------------------ *
 * Options. Both additions are individually tunable and individually off-able:
 * at zero, the GLSL for them is never emitted and the uniforms are never
 * declared, so a disabled effect costs nothing at all.
 * ------------------------------------------------------------------------ */

/** Ceiling on the ripple slots, and so on the shader's inner loop. */
const IMPACT_MAX_SLOTS = 8;

/** Felt impact response — the ripple a landing card puts into the felt. */
export interface TableImpactOptions {
  /**
   * Peak tilt of the felt's normal at the crest, at full strength. A well wall
   * pushes the normal about 0.7, so this stays a fraction of that: it should
   * read as the felt flexing, not as a pond.
   */
  amplitude?: number;
  /**
   * Peak modulation of the key light on the crest, at full strength: the face
   * turned into the light takes this much more of it, the lee face this much
   * less. On a near-black felt this carries most of what the player actually
   * sees — see `rippleBlock` for why the tilt alone cannot.
   */
  keyCatch?: number;
  /** Simultaneous ripples, oldest recycled once they are all busy. */
  slots?: number;
  /** Seconds from touchdown to dead. */
  duration?: number;
  /** How far the crest travels in design units before it dies. */
  radius?: number;
  /** Half-width of the crest at birth, in design units. */
  width?: number;
  /** Extra half-width by the end of its life — the felt damping it out. */
  spread?: number;
  /**
   * Strength multiplier under `prefers-reduced-motion`. Left at a whisper
   * rather than zero: this is a 200ms crease in a surface, not a camera move,
   * and the acknowledgement is worth keeping. Set to 0 to drop it entirely.
   */
  reducedMotionScale?: number;
}

/** Making the light source visible: its pool on the felt, and its beam. */
export interface TableLightOptions {
  /**
   * Multiplicative lift of the felt's key response at the centre of the pool,
   * falling to zero at its edge. Multiplicative on purpose — the weave and the
   * mottle ride with it instead of being airbrushed over. Zero disables.
   */
  pool?: number;
  /** Additive haze inside the pool, in linear light. Keep it tiny. */
  poolHaze?: number;
  /** Pool minor radius as a fraction of the felt's half-diagonal. */
  poolSize?: number;
  /** Additive haze along the beam, in linear light. Zero disables the shaft. */
  shaft?: number;
  /** Tangent of the beam's half-angle. Wider than ~0.8 stops being a beam. */
  shaftSpread?: number;
  /** Design units from the lamp to the densest part of the shaft. */
  shaftReach?: number;
}

export interface TableOptions {
  impact?: TableImpactOptions;
  light?: TableLightOptions;
}

interface ImpactConfig {
  readonly amplitude: number;
  readonly keyCatch: number;
  readonly slots: number;
  readonly duration: number;
  readonly radius: number;
  readonly width: number;
  readonly spread: number;
  readonly reducedMotionScale: number;
}

interface LightConfig {
  readonly pool: number;
  readonly poolHaze: number;
  readonly poolSize: number;
  readonly shaft: number;
  readonly shaftSpread: number;
  readonly shaftReach: number;
}

/**
 * Defaults.
 *
 * `radius` is set so the crest clears a card's own footprint early — the card
 * is 200 wide, so the first ~40ms of the ripple happens underneath it and what
 * the player sees is a crease *emerging* from under the card that just landed.
 */
const IMPACT_DEFAULTS: ImpactConfig = {
  amplitude: 0.4,
  keyCatch: 0.45,
  slots: 4,
  duration: 0.22,
  radius: 300,
  width: 16,
  spread: 26,
  reducedMotionScale: 0.35,
};

/**
 * Defaults.
 *
 * The felt sits around 0.02 in linear light, so the additive numbers here are
 * necessarily tiny: `poolHaze` at 0.0035 is about a twelve-level lift in sRGB
 * at the centre of the table and nothing at all at its edges, which is the
 * difference between "a lamp hangs over this table" and "someone turned the
 * brightness up".
 */
const LIGHT_DEFAULTS: LightConfig = {
  pool: 0.45,
  poolHaze: 0.002,
  poolSize: 0.52,
  shaft: 0.006,
  shaftSpread: 0.36,
  shaftReach: 330,
};

/** An override, or the default when the caller left it out or passed nonsense. */
function option(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value;
}

function resolveImpact(options: TableImpactOptions | undefined): ImpactConfig {
  const o = options ?? {};
  const amplitude = Math.max(0, option(o.amplitude, IMPACT_DEFAULTS.amplitude));
  // Capped below 1: at 1 the lee face of a crest takes no key light at all,
  // which is a hole in the felt rather than a crease in it.
  const keyCatch = clamp(option(o.keyCatch, IMPACT_DEFAULTS.keyCatch), 0, 0.9);
  const slots = clamp(Math.round(option(o.slots, IMPACT_DEFAULTS.slots)), 0, IMPACT_MAX_SLOTS);
  const silent = amplitude <= 0 && keyCatch <= 0;

  return {
    // A ripple with nothing to show and a ripple with nowhere to live are the
    // same thing, collapsed here so every other test is simply `slots > 0`.
    amplitude,
    keyCatch,
    slots: silent ? 0 : slots,
    duration: Math.max(0.02, option(o.duration, IMPACT_DEFAULTS.duration)),
    radius: Math.max(1, option(o.radius, IMPACT_DEFAULTS.radius)),
    width: Math.max(1, option(o.width, IMPACT_DEFAULTS.width)),
    spread: Math.max(0, option(o.spread, IMPACT_DEFAULTS.spread)),
    reducedMotionScale: clamp(
      option(o.reducedMotionScale, IMPACT_DEFAULTS.reducedMotionScale),
      0,
      1,
    ),
  };
}

function resolveLight(options: TableLightOptions | undefined): LightConfig {
  const o = options ?? {};
  return {
    pool: Math.max(0, option(o.pool, LIGHT_DEFAULTS.pool)),
    poolHaze: Math.max(0, option(o.poolHaze, LIGHT_DEFAULTS.poolHaze)),
    poolSize: Math.max(0.05, option(o.poolSize, LIGHT_DEFAULTS.poolSize)),
    shaft: Math.max(0, option(o.shaft, LIGHT_DEFAULTS.shaft)),
    shaftSpread: Math.max(0.02, option(o.shaftSpread, LIGHT_DEFAULTS.shaftSpread)),
    shaftReach: Math.max(1, option(o.shaftReach, LIGHT_DEFAULTS.shaftReach)),
  };
}

/* ------------------------------------------------------------------------ *
 * Colour helpers. The shader works in linear light; tokens are sRGB.
 * ------------------------------------------------------------------------ */

function channels(hex: number): [number, number, number] {
  return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
}

function toLinear(c: number): number {
  return Math.pow(c, 2.2);
}

/** Blend two token colours in sRGB space and return a new packed hex. */
function mixHex(a: number, b: number, t: number): number {
  const [ar, ag, ab] = channels(a);
  const [br, bg, bb] = channels(b);
  const r = Math.round((ar + (br - ar) * t) * 255);
  const g = Math.round((ag + (bg - ag) * t) * 255);
  const bl = Math.round((ab + (bb - ab) * t) * 255);
  return (r << 16) | (g << 8) | bl;
}

/**
 * Format a JS number as a GLSL float literal, always with a decimal point and
 * never rounded to zero — the light falloff constants are around 4e-7, so a
 * naive `toFixed` would silently delete the entire attenuation model.
 */
function num(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '0.0';

  const magnitude = Math.abs(v);
  if (magnitude >= 1e-4 && magnitude < 1e7) {
    const fixed = v.toFixed(9).replace(/0+$/, '');
    return fixed.endsWith('.') ? `${fixed}0` : fixed;
  }

  const [mantissa, exponent] = v.toExponential(9).split('e');
  const trimmed = mantissa.replace(/0+$/, '');
  const safe = trimmed.endsWith('.') ? `${trimmed}0` : trimmed;
  return `${safe}e${exponent}`;
}

function vec2(x: number, y: number): string {
  return `vec2(${num(x)}, ${num(y)})`;
}

function vec3(x: number, y: number, z: number): string {
  return `vec3(${num(x)}, ${num(y)}, ${num(z)})`;
}

/** A token colour as a linear-space GLSL constant. */
function linear3(hex: number): string {
  const [r, g, b] = channels(hex);
  return vec3(toLinear(r), toLinear(g), toLinear(b));
}

/**
 * A colour reduced to pure hue at unit brightness, then pulled `toWhite`
 * toward neutral. Used to tint a specular lobe: it keeps the material's hue in
 * its own highlight without changing how bright that highlight is.
 */
function sheen(hex: number, toWhite: number): string {
  const rgb = channels(hex);
  const peak = Math.max(...rgb) || 1;
  const [r, g, b] = rgb.map((c) => c / peak + (1 - c / peak) * toWhite);
  return vec3(r, g, b);
}

/** A token colour written into a linear-space uniform slot. */
function writeLinear(target: Float32Array, offset: number, hex: number): void {
  const [r, g, b] = channels(hex);
  target[offset] = toLinear(r);
  target[offset + 1] = toLinear(g);
  target[offset + 2] = toLinear(b);
}

/* ------------------------------------------------------------------------ *
 * Procedural fabric: a seamless weave packed as (nx, ny, height, 1).
 * ------------------------------------------------------------------------ */

/** Integer hash on a wrapping lattice, so the noise tiles exactly. */
function latticeHash(ix: number, iy: number, period: number, seed: number): number {
  const x = ((ix % period) + period) % period;
  const y = ((iy % period) + period) % period;
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

/** Value noise on the same wrapping lattice. `period` lattice cells per tile. */
function tilingNoise(x: number, y: number, period: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = latticeHash(x0, y0, period, seed);
  const b = latticeHash(x0 + 1, y0, period, seed);
  const c = latticeHash(x0, y0 + 1, period, seed);
  const d = latticeHash(x0 + 1, y0 + 1, period, seed);
  const top = a + (b - a) * ux;
  const bottom = c + (d - c) * ux;
  return top + (bottom - top) * uy;
}

/**
 * Builds the felt height field: a plain over/under weave plus three octaves of
 * tiling noise for paper-dry tooth. Returned as a flat `size * size` array.
 */
function buildHeightField(size: number, cell: number): Float32Array {
  const height = new Float32Array(size * size);
  const inv = 1 / size;

  for (let y = 0; y < size; y++) {
    const cy = Math.floor(y / cell);
    const v = (y % cell) / cell;
    const weft = Math.pow(Math.sin(Math.PI * v), 0.8);

    for (let x = 0; x < size; x++) {
      const cx = Math.floor(x / cell);
      const u = (x % cell) / cell;
      const warp = Math.pow(Math.sin(Math.PI * u), 0.8);

      // Plain weave: which thread is on top alternates per crossing.
      const warpOnTop = ((cx + cy) & 1) === 0;
      const top = warpOnTop ? warp : weft;
      const under = warpOnTop ? weft : warp;

      let h = 0.3 + 0.52 * top + 0.14 * under;
      h += 0.1 * (tilingNoise(x * inv * 64, y * inv * 64, 64, 11) - 0.5);
      h += 0.07 * (tilingNoise(x * inv * 16, y * inv * 16, 16, 29) - 0.5);
      h += 0.05 * (tilingNoise(x * inv * 128, y * inv * 128, 128, 71) - 0.5);

      height[y * size + x] = h < 0 ? 0 : h > 1 ? 1 : h;
    }
  }

  return height;
}

/**
 * Rasterises the height field into an RGBA texture:
 * R/G = tangent-space normal xy, B = height (albedo + AO), A = opaque.
 *
 * The alpha channel is deliberately left at 255 — canvas 2D stores premultiplied
 * pixels, so packing data into alpha would corrupt the other three channels.
 */
function createFabricTexture(): Texture {
  const size = FABRIC_TILE;
  const height = buildHeightField(size, FABRIC_CELL);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('table: 2D canvas context unavailable for felt texture');

  const image = ctx.createImageData(size, size);
  const data = image.data;
  const wrap = (n: number): number => (n + size) % size;

  for (let y = 0; y < size; y++) {
    const up = wrap(y - 1) * size;
    const down = wrap(y + 1) * size;
    const row = y * size;

    for (let x = 0; x < size; x++) {
      const left = wrap(x - 1);
      const right = wrap(x + 1);

      // Height rises toward the viewer (+z), so n = normalize(-dh/dx, -dh/dy, 1).
      const nx = (height[row + left] - height[row + right]) * FABRIC_NORMAL_STRENGTH;
      const ny = (height[up + x] - height[down + x]) * FABRIC_NORMAL_STRENGTH;

      const i = (row + x) * 4;
      data[i] = Math.round(Math.min(1, Math.max(0, nx * 0.5 + 0.5)) * 255);
      data[i + 1] = Math.round(Math.min(1, Math.max(0, ny * 0.5 + 0.5)) * 255);
      data[i + 2] = Math.round(height[row + x] * 255);
      data[i + 3] = 255;
    }
  }

  ctx.putImageData(image, 0, 0);

  const source = new CanvasSource({
    resource: canvas,
    label: 'table-fabric',
    addressMode: 'repeat',
    scaleMode: 'linear',
    autoGenerateMipmaps: true,
    alphaMode: 'no-premultiply-alpha',
  });

  return new Texture({ source, label: 'table-fabric' });
}

/* ------------------------------------------------------------------------ *
 * GLSL.
 * ------------------------------------------------------------------------ */

const VERTEX = /* glsl */ `
in vec2 aPosition;
in vec2 aUV;

out vec2 vUV;
out vec2 vPos;
out vec4 vColor;
out float vPxScale;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec4 uWorldColorAlpha;
uniform vec4 uColor;
uniform vec2 uResolution;

void main(void) {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;

    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);

    vUV = aUV;
    vPos = aPosition;
    vColor = uColor * uWorldColorAlpha;

    // Screen pixels per design unit, so the fragment shader can antialias its
    // signed-distance edges without needing derivative extensions.
    vec3 originClip = mvp * vec3(0.0, 0.0, 1.0);
    vec3 stepClip = mvp * vec3(1.0, 0.0, 1.0);
    vec2 stepPx = vec2(
        (stepClip.x - originClip.x) * uResolution.x,
        (stepClip.y - originClip.y) * uResolution.y
    ) * 0.5;
    vPxScale = max(length(stepPx), 0.0001);
}
`;

/**
 * Emits the unrolled block for one recess. Loop indices are legal array
 * subscripts in ESSL 1.00 but literal constants are legal *everywhere*, so the
 * wells are generated rather than looped.
 */
function wellBlock(
  index: number,
  centreX: number,
  centreY: number,
  options: { dynamic: boolean; depth: number; hairAlpha: number; extend?: number },
): string {
  const { dynamic, depth, hairAlpha } = options;
  const wellExtend = options.extend ?? 0;

  const state = dynamic
    ? `
        float hov = uWellState[${index}].x;
        float wld = uWellState[${index}].y;
        float pls = uWellState[${index}].z;
        vec2 tlt = uWellTilt[${index}];`
    : `
        float hov = 0.0;
        float wld = 0.0;`;

  const glow = dynamic
    ? `
        // The pool is offset a touch below centre so it reads as light welling
        // up out of the recess rather than a ring painted around it.
        vec2 qg = q - tlt * ${num(WELL_PARALLAX)};
        float rad = length(vec2(
            qg.x / ${num(WELL_HALF_X)},
            (qg.y - ${num(WELL_HALF_Y * 0.14)} + ${num(wellExtend * 0.5)}) / ${num(WELL_HALF_Y)}
        ));
        float pool = 1.0 - smoothstep(0.10, 1.05, rad);
        float bleed = exp(-max(d, 0.0) / ${num(WELL_BLEED)});
        float field = clamp(pool + bleed * 0.45, 0.0, 1.0);
        float rimD = d / max(3.5, aa * 2.0);
        float rim = exp(-rimD * rimD);

        emissive += HOVER_COL * (hov * (field * ${num(HOVER_POOL)} + rim * ${num(HOVER_RIM)}));
        emissive += WILD_COL * (wld * (field * ${num(WILD_POOL)} + rim * ${num(WILD_RIM)}));
        emissive += uWellPulse[${index}]
            * (pls * (field * ${num(PULSE_POOL)} + rim * ${num(PULSE_RIM)}));

        feltNormal.xy += tlt * (${num(WELL_TILT_NORMAL)} * wIn);`
    : '';

  return /* glsl */ `
    {${state}

        // The recess has to CONTAIN the play, not just mark where it starts.
        //
        // A card-sized well ends at PILE_Y + 155, but a full five-card fan
        // reaches PILE_Y + 4*PILE_FAN + 145 = +859 — so 214 design units, about
        // three quarters of a card, sat on undifferentiated felt with no bed
        // under it. The wells only read as recesses while empty or holding one
        // card; the moment a pile grew, the stack looked like it had slid out
        // of its holder.
        //
        // Extended DOWNWARD only, which is deliberate: WELL_BLEED and
        // TOTAL_BADGE.offsetY are both derived from the well's TOP edge, so an
        // asymmetric extension leaves the badge clearance untouched. Piles are
        // 345 apart against a 220-wide well, so nothing moves laterally either.
        vec2 q = vPos - ${vec2(centreX, centreY + wellExtend * 0.5)};
        vec2 wellHalf = WELL_HALF + vec2(0.0, ${num(wellExtend * 0.5)});
        float d = sdRoundBox(q, wellHalf, ${num(WELL_RADIUS)});
        vec2 g = roundBoxGrad(q, wellHalf, ${num(WELL_RADIUS)});

        // Inner wall. The side facing the light turns away from it and darkens,
        // the far side turns into it and lights up — that is the whole trick.
        float insideT = clamp(-d / ${num(WELL_WALL)}, 0.0, 1.0);
        float wallSlope = 6.0 * insideT * (1.0 - insideT);
        feltNormal.xy -= g * (${num(depth)} * wallSlope / ${num(WELL_WALL)});

        // A hair of raised felt pushed up around the rim.
        float lipD = (d - ${num(WELL_LIP_OFFSET)}) / ${num(WELL_LIP_FALL)};
        float lipE = exp(-lipD * lipD);
        feltNormal.xy += g * (${num((WELL_LIP * 2) / WELL_LIP_FALL)} * lipD * lipE);

        float wIn = smoothstep(0.0, 1.0, clamp(-d / 22.0, 0.0, 1.0));

        // Occlusion, not paint.
        //
        // Most of the well's darkening used to come from a flat albedo
        // multiply, which does not deepen where the recess is deepest, does not
        // respond to the key, and would look identical if the lamp moved. The
        // ambient cut alongside it was worth about half an 8-bit level, because
        // ambient is only ~17% of the pixel here.
        //
        // Now the cut lands mostly on the key path — which is 79% of the pixel
        // — in proportion to how much of the near wall stands between the floor
        // and the lamp. So the recess is directional.
        feltAo *= 1.0 - 0.42 * wIn;
        feltShadow *= 1.0 - 0.20 * wIn * max(dot(g, keyXY), 0.0);

        // And the lit far wall bounces back onto the floor, so the floor runs
        // from shadowed at the near edge to lifted at the far one instead of
        // being one flat grey rectangle.
        feltAlbedo *= 1.0 + 0.16 * wIn * max(dot(-g, keyXY), 0.0);
        feltAlbedo = mix(feltAlbedo, feltAlbedo * 0.86, wIn);

        float hairW = max(${num(WELL_HAIR_WIDTH)}, aa * 1.1);
        float hairD = (d + ${num(WELL_HAIR_OFFSET)}) / hairW;
        float hair = exp(-hairD * hairD);
        emissive += GOLD * (hair * ${num(hairAlpha)} * (1.0 + 1.8 * hov + 1.3 * wld));
${glow}
    }`;
}

/**
 * The felt's answer to a card landing: a travelling crease in its normal.
 *
 * Each live slot contributes the derivative of a gaussian bump — one crest
 * chased by one trough — riding outward on an ease-out radius and damping as it
 * goes. Nothing here touches albedo or emissive; the crease is visible only
 * because of what it does to the key light, which is the difference between a
 * crease in fabric and a ring drawn on it.
 *
 * It does that in two ways, and it needs both. Tilting the normal is the honest
 * half, but this felt is near-black: at 0.02 linear, a plausible tilt moves the
 * diffuse term by about one 8-bit level and the whole thing disappears under
 * the dither. So the crest also *catches* the key — the face turned into the
 * light takes more of it and the lee face falls into its own shadow, through
 * the same occlusion channel the rail already uses and against the same
 * in-plane light direction. That asymmetry is what makes it read as raised
 * fabric rather than as a glowing ring, and it is why the arc facing the lamp
 * goes warm while the far arc goes cool. Cranking the tilt alone to reach the
 * same contrast would have needed a slope no felt could hold.
 *
 * The whole set sits behind one uniform-driven branch. It is coherent across
 * every fragment in the draw, so a table with nothing landing on it skips the
 * block outright rather than summing four ripples of strength zero.
 */
function rippleBlock(impact: ImpactConfig): string {
  if (impact.slots === 0) return '';

  const slots: string[] = [];
  for (let i = 0; i < impact.slots; i++) {
    slots.push(/* glsl */ `
        {
            vec4 rp = uRipple[${i}];
            vec2 q = vPos - rp.xy;
            float d = length(q);
            float fade = 1.0 - rp.z;

            // Ease-out radius: the crest leaves fast and slows as it dies, so
            // most of its life is spent out where the card is not covering it.
            float r = ${num(impact.radius)} * (1.0 - fade * fade * fade);
            float w = ${num(impact.width)} + rp.z * ${num(impact.spread)};
            float x = (d - r) / w;
            float slope = -2.0 * x * exp(-x * x) * (rp.w * fade * fade);

            // max(d, 1.0) rather than a true normalize: it is the same unit
            // vector everywhere the crest can actually be, and it cannot blow
            // up in the half-pixel at the impact point itself.
            crest += q * (slope / max(d, 1.0));
        }`);
  }

  return /* glsl */ `
    if (uRippleGate > 0.5) {
        vec2 crest = vec2(0.0);
${slots.join('\n')}
        feltNormal.xy += crest * ${num(impact.amplitude)};

        // Clamped, because two ripples crossing must lift and dim the felt,
        // never punch a hole in it or blow a highlight through it.
        feltShadow *= clamp(
            1.0 + ${num(impact.keyCatch)} * dot(crest, keyXY),
            ${num(1 - impact.keyCatch)},
            ${num(1 + impact.keyCatch)}
        );
    }
`;
}

/**
 * The pool the lamp actually casts on the felt.
 *
 * Applied mostly as a gain on the felt's key response rather than as an
 * additive wash, so the weave, the mottle and the wells all ride with it; the
 * additive part is a whisper of haze. Both fall to exactly zero at the pool's
 * edge, which is what keeps the table's black point where it was.
 */
function poolBlock(light: LightConfig): string {
  if (light.pool <= 0 && light.poolHaze <= 0) return '';

  const minor = light.poolSize * FELT_HALF_DIAGONAL;
  const major = minor / BEAM_COS;

  return /* glsl */ `
    {
        vec2 pq = vPos - ${vec2(AIM_X, AIM_Y)};
        float pu = dot(pq, BEAM_AXIS) / ${num(major)};
        float pv = dot(pq, BEAM_PERP) / ${num(minor)};
        // Squared shoulder: a soft core with no visible edge to the pool.
        float pool = 1.0 - smoothstep(0.0, 1.0, sqrt(pu * pu + pv * pv));
        pool *= pool;

        // A remap, not a lift: the pool has to DARKEN outside as well as
        // brighten inside, or it cannot out-run the key's own falloff. As a
        // pure lift the peak gain was 1.28x and the net result was a table with
        // roughly half a stop of variation, in the wrong place. The ambient
        // term deliberately bypasses the gain, so the dark felt still floors at
        // about 17% of centre and the corners cannot crush to black.
        feltGain *= mix(1.0 - ${num(light.pool * 0.85)}, 1.0 + ${num(light.pool)}, pool);

        // The lamp is never perfectly steady.
        //
        // Between turns — which in a card game is most of the time — the table
        // surface was a bit-identical still image: every animated path here is
        // event-driven, so with no card moving and no pointer on a pile,
        // nothing on the felt changed at all. The eye reads that as a
        // screenshot. Two incommensurate rates so it never visibly loops, at an
        // amplitude of about one 8-bit level on the centre felt: deliberately
        // at the edge of conscious detection, which is what a real lamp does.
        feltGain *= 1.0 + 0.018 * sin(uTime * 0.62) + 0.009 * sin(uTime * 1.47 + 2.1);
        emissive += KEY_COL * (${num(light.poolHaze)} * pool);
    }
`;
}

/**
 * A hint of the beam itself — haze in the air between the lamp and the table,
 * drawn on the surface it lands on. A cone from LIGHT's ground projection,
 * densest one reach-length in and gone well before the far rail, so it says
 * "the light comes in from up there" and then stops talking.
 */
function shaftBlock(light: LightConfig): string {
  if (light.shaft <= 0) return '';

  const invSpread2 = 1 / (light.shaftSpread * light.shaftSpread);

  return /* glsl */ `
    {
        vec2 sq = vPos - ${vec2(LIGHT.x, LIGHT.y)};
        float along = dot(sq, BEAM_AXIS);
        float across = dot(sq, BEAM_PERP);

        // Angular falloff without an acos: across-over-along is the tangent of
        // the angle off the beam's axis, and that is all the wedge needs.
        float off = across / max(along, 1.0);
        float wedge = exp(-off * off * ${num(invSpread2)});

        // Zero at the lamp, peaking one reach in, and cubed so the tail is
        // gone by the middle of the table instead of washing the whole felt.
        // Anything behind the lamp gets depth 0 and drops out on its own.
        float depth = max(along, 0.0) / ${num(light.shaftReach)};
        float body = depth * exp(1.0 - depth);
        body = body * body * body;

        tableColour += KEY_COL * (${num(light.shaft)} * wedge * body);
    }
`;
}

function buildFragment(impact: ImpactConfig, light: LightConfig): string {
  const centre = { x: DESIGN.width / 2, y: DESIGN.height / 2 };
  const half = {
    x: DESIGN.width / 2 - TABLE.inset,
    y: DESIGN.height / 2 - TABLE.inset,
  };

  const wells: string[] = [];
  for (let i = 0; i < PILE_COUNT; i++) {
    const p = pilePosition(i);
    wells.push(
      wellBlock(i, p.x, p.y, {
        dynamic: true,
        depth: WELL_DEPTH,
        hairAlpha: WELL_HAIR_ALPHA,
        extend: WELL_EXTEND_Y,
      }),
    );
  }
  wells.push(
    wellBlock(-1, STOCK.x, STOCK.y, {
      dynamic: false,
      depth: STOCK_DEPTH,
      hairAlpha: STOCK_HAIR_ALPHA,
    }),
  );

  // NOTE: `precision` must be the very first token. Pixi's GlProgram
  // preprocessor only downgrades highp→mediump on devices that cannot do
  // highp when the source *starts* with the precision statement.
  return /* glsl */ `precision highp float;

in vec2 vUV;
in vec2 vPos;
in vec4 vColor;
in float vPxScale;

out vec4 finalColor;

uniform sampler2D uFabric;
uniform vec4 uWellState[${PILE_COUNT}];
uniform vec3 uWellPulse[${PILE_COUNT}];
uniform vec2 uWellTilt[${PILE_COUNT}];
uniform float uTime;
// The run's colour and how far the felt leans toward it: rgb is linear-space,
// w is 0..1. Written by Table.setStreakTint from the aura's own eased level.
uniform vec4 uStreak;
${
  impact.slots > 0
    ? `uniform vec4 uRipple[${impact.slots}];
uniform float uRippleGate;`
    : ''
}

const float PI = 3.14159265;

// --- geometry, straight out of @/pixi/layout -------------------------------
const vec2 TABLE_C = ${vec2(centre.x, centre.y)};
const vec2 TABLE_HALF = ${vec2(half.x, half.y)};
const float TABLE_R = ${num(TABLE.radius)};
const float RAIL = ${num(TABLE.railThickness)};
const vec2 WELL_HALF = ${vec2(WELL_HALF_X, WELL_HALF_Y)};
const vec2 FABRIC_TILES = ${vec2(DESIGN.width / FABRIC_WORLD, DESIGN.height / FABRIC_WORLD)};
const vec2 RAIL_GRAIN_TILES = ${vec2(DESIGN.width / RAIL_GRAIN_WORLD, DESIGN.height / RAIL_GRAIN_WORLD)};

// --- the one light, straight out of @/pixi/layout --------------------------
const vec3 KEY_POS = ${vec3(LIGHT.x, LIGHT.y, LIGHT.z)};
const vec3 KEY_COL = ${linear3(LIGHT.colour)};
const float KEY_INT = ${num(LIGHT.intensity)};
const float KEY_INV_REF2 = 1.0 / (${num(KEY_REF)} * ${num(KEY_REF)});
const vec3 FILL_POS = ${vec3(LIGHT.fill.x, LIGHT.fill.y, LIGHT.fill.z)};
const vec3 FILL_COL = ${linear3(LIGHT.fill.colour)};
const float FILL_INT = ${num(LIGHT.fill.intensity)};
const float FILL_INV_REF2 = 1.0 / (${num(FILL_REF)} * ${num(FILL_REF)});
const float AMBIENT = ${num(LIGHT.ambient)};
const vec3 EYE = ${vec3(DESIGN.width / 2, DESIGN.height / 2, EYE_Z)};

// The beam's footprint on the table plane: unit direction from the lamp toward
// what it is aimed at, and its perpendicular. Derived from LIGHT, so the pool
// and the shaft below cannot disagree with the shading or the card shadows.
const vec2 BEAM_AXIS = ${vec2(BEAM_AXIS_X, BEAM_AXIS_Y)};
const vec2 BEAM_PERP = ${vec2(BEAM_PERP_X, BEAM_PERP_Y)};

// --- materials, straight out of @/core/tokens ------------------------------
const vec3 FELT_BASE = ${linear3(palette.felt)};
const vec3 FELT_DEEP = ${linear3(palette.feltDeep)};
const vec3 RAIL_BASE = ${linear3(RAIL_BASE)};
const vec3 GOLD = ${linear3(palette.gold)};
const vec3 GOLD_DEEP = ${linear3(palette.goldDeep)};
const vec3 WILD_COL = ${linear3(palette.violetLight)};
const vec3 HOVER_COL = ${linear3(mixHex(LIGHT.colour, palette.gold, 0.7))};
const vec3 FELT_SHEEN = ${sheen(palette.felt, 0.45)};
const vec3 RAIL_SHEEN = vec3(1.0);

// --------------------------------------------------------------------------

float sdRoundBox(vec2 p, vec2 b, float r) {
    vec2 q = abs(p) - b + r;
    return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
}

/** Outward unit gradient of the rounded box field above. */
vec2 roundBoxGrad(vec2 p, vec2 b, float r) {
    vec2 s = vec2(p.x < 0.0 ? -1.0 : 1.0, p.y < 0.0 ? -1.0 : 1.0);
    vec2 q = abs(p) - b + r;
    vec2 m = max(q, 0.0);
    float len = length(m);

    vec2 g;
    if (len > 0.0001) {
        g = m / len;
    } else if (q.x > q.y) {
        g = vec2(1.0, 0.0);
    } else {
        g = vec2(0.0, 1.0);
    }
    return g * s;
}

float hash21(vec2 p) {
    vec3 p3 = fract(vec3(p.x, p.y, p.x) * 0.1031);
    p3 += dot(p3, vec3(p3.y, p3.z, p3.x) + 33.33);
    return fract((p3.x + p3.y) * p3.z);
}

float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = hash21(i);
    float b = hash21(i + vec2(1.0, 0.0));
    float c = hash21(i + vec2(0.0, 1.0));
    float d = hash21(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

/**
 * One shading model for the whole table: key + fill + ambient, Blinn half
 * vector against the virtual eye, all in linear light. Every surface here goes
 * through this function, which is why nothing can disagree with anything else.
 */
vec3 shade(
    vec2 p,
    vec3 n,
    vec3 albedo,
    float specInt,
    float specPow,
    vec3 specTint,
    float ao,
    float shadow,
    float gain
) {
    vec3 p3 = vec3(p, 0.0);

    vec3 toKey = KEY_POS - p3;
    float keyDist = length(toKey);
    vec3 L = toKey / keyDist;
    float keyAtt = 1.0 / (1.0 + keyDist * keyDist * KEY_INV_REF2);
    vec3 key = KEY_COL * (KEY_INT * keyAtt * shadow);

    float ndl = max(dot(n, L), 0.0);
    vec3 v = normalize(EYE - p3);
    vec3 h = normalize(L + v);
    float spec = pow(max(dot(n, h), 0.0), specPow) * specInt;

    vec3 toFill = FILL_POS - p3;
    float fillDist = length(toFill);
    vec3 FL = toFill / fillDist;
    float fillAtt = 1.0 / (1.0 + fillDist * fillDist * FILL_INV_REF2);
    float fndl = max(dot(n, FL), 0.0);

    vec3 lit = albedo * key * (ndl * gain);
    lit += key * specTint * spec;
    lit += albedo * FILL_COL * (FILL_INT * fillAtt * fndl * gain);
    lit += albedo * (AMBIENT * ao * (0.70 + 0.30 * n.z));
    return lit;
}

void main(void) {
    // Design units per screen pixel — the antialias width for every SDF edge.
    float aa = clamp(1.0 / vPxScale, 0.35, 3.0);
    float aaHalf = aa * 0.75;

    vec2 rel = vPos - TABLE_C;
    float dOut = sdRoundBox(rel, TABLE_HALF, TABLE_R);

    float tableMask = 1.0 - smoothstep(-aaHalf, aaHalf, dOut);
    if (tableMask <= 0.0) {
        finalColor = vec4(0.0);
        return;
    }

    // A rounded rect offset inward by t is exactly its field plus t, so the
    // felt boundary comes for free from the outer one.
    float dFelt = dOut + RAIL;
    vec2 gOut = roundBoxGrad(rel, TABLE_HALF, TABLE_R);
    vec2 keyXY = normalize(KEY_POS.xy - vPos);

    // Both fetches are unconditional: sampling with an implicit LOD inside
    // non-uniform control flow has undefined derivatives.
    vec4 fabric = texture(uFabric, vUV * FABRIC_TILES);
    vec4 grain = texture(uFabric, vUV * RAIL_GRAIN_TILES);

    // ---------------------------------------------------------------- felt
    float fabH = fabric.b;

    vec2 weaveG = fabric.rg * 2.0 - 1.0;
    vec3 feltNormal = vec3(weaveG, 1.0);
    vec3 feltAlbedo = mix(FELT_DEEP, FELT_BASE, clamp(0.25 + 0.85 * fabH, 0.0, 1.0));

    // Large-scale variation: a table with a history.
    //
    // The mottle below runs at 238 and 90 design units, so the LOWEST spatial
    // frequency anywhere in the felt was about an eighth of the table — every
    // 250-unit patch was statistically the same patch, which is what makes a
    // surface read as procedural. These two terms are the only things here at
    // table scale, and both are anchored to real geometry rather than to noise.
    //
    // The dealing lane runs stock-to-piles and is worn slightly brighter, the
    // way a lane a shoe deals across actually polishes.
    vec2 lane = vPos - vec2(700.0, 560.0);
    float laneU = dot(lane, normalize(vec2(1.0, -0.06))) / 640.0;
    float laneV = dot(lane, normalize(vec2(0.06, 1.0))) / 210.0;
    float wear = exp(-(laneU * laneU + laneV * laneV));
    // One octave of grime at 1.6 cells across the whole table.
    float grime = vnoise(vPos * 0.00085 + 41.0);

    float mottle = vnoise(vPos * 0.0042) * 0.66 + vnoise(vPos * 0.011) * 0.34;
    feltAlbedo *= 0.90 + 0.22 * mottle;
    feltAlbedo *= 1.0 + 0.13 * wear;
    feltAlbedo *= 0.93 + 0.14 * grime;
    float feltAo = 0.72 + 0.28 * fabH;

    // The rail occludes the key light exactly where it sits between the felt
    // and the light, so the inner shadow lands on the upper-left by itself.
    float edgeProx = 1.0 - smoothstep(0.0, ${num(RAIL_SHADOW_REACH)}, max(-dFelt, 0.0));
    float occl = max(dot(gOut, keyXY), 0.0);
    float feltShadow = 1.0 - ${num(RAIL_SHADOW_STRENGTH)} * edgeProx * occl;

    // The rail occludes AMBIENT on every side, not only the lit one. The
    // directional term above is exactly zero on the bottom rail (gOut faces
    // away from the key), so the felt received no darkening at all approaching
    // the lower edge and the table lost its silhouette right where the HUD and
    // the player's hand are. Occlusion is not a function of the key.
    feltAo *= 1.0 - 0.38 * edgeProx;

    // Weave self-shadowing.
    //
    // The fabric normal was feeding the diffuse term, but at this felt's
    // brightness that path moves the pixel by about one 8-bit level and
    // disappears under the film grain — so what actually survived was the
    // height-to-albedo mapping above, which is a baked value pattern identical
    // everywhere and unaware of where the light is. This is the same remedy the
    // ripple already uses: put the weave onto the shadow term, which multiplies
    // the key term directly, so the flank turned into the lamp takes more of it
    // and the lee flank falls into its own shadow. It also flips sign across
    // the table as the key direction rotates, which is what makes the cloth
    // look different near the lamp than in the far corner.
    feltShadow *= clamp(1.0 + 0.55 * dot(weaveG, keyXY), 0.55, 1.45);

    // ------------------------------------------------- cards landing on it
${rippleBlock(impact)}
    vec3 emissive = vec3(0.0);
    float feltGain = ${num(FELT_GAIN)};

    // ------------------------------------------------- the run's own colour
    //
    // The fourth streak channel, and the quietest: the room itself knowing that
    // a run is alive. Two things happen, and the split matters.
    //
    // **The hue rotates, it does not mix.** The tint is divided by its own
    // Rec. 709 luminance first, so what is mixed into the albedo carries the
    // colour and no brightness at all. A straight mix toward the ladder colour
    // would have lifted the table rather than warming it — gold's albedo is an
    // order of magnitude above plum's, so even a tenth of a mix roughly doubles
    // the felt, and the black point this whole material is built around would
    // move every time a run opened.
    //
    // **The gain carries the brightness, and it has to, because the felt is very
    // nearly black.** This was measured rather than assumed. With the rotation
    // alone at the aura's own 0.10 ceiling, a captured frame of a live 6x run
    // and a captured frame of the same table with no run differed by **one
    // 8-bit level** — under the dither on the line below it, i.e. not there at
    // all. The two constants below take that 0.10 to about a 26% gain and a 40%
    // rotation, which moves the cloth from 0x140E25 to roughly 0x180F2E: a
    // violet lean a player would feel and would never call a wash.
    //
    // Gated on a uniform rather than branchless: the branch is coherent across
    // the entire draw, so it costs nothing, and it is taken on none of the
    // frames in a round where no run is alive.
    if (uStreak.a > 0.0) {
        float tintLuma = max(dot(uStreak.rgb, vec3(0.2126, 0.7152, 0.0722)), 1e-4);
        feltAlbedo *= mix(vec3(1.0), uStreak.rgb / tintLuma, min(uStreak.a * ${num(STREAK_ROTATE)}, 1.0));
        feltGain *= 1.0 + uStreak.a * ${num(STREAK_GAIN)};
    }

    // ------------------------------------------------ the lamp's own pool
${poolBlock(light)}
${wells.join('\n')}

    vec3 feltColour = shade(
        vPos,
        normalize(feltNormal),
        feltAlbedo,
        ${num(FELT_SPEC)},
        ${num(FELT_SPEC_POW)},
        FELT_SHEEN,
        max(feltAo, 0.0),
        feltShadow,
        feltGain
    );
    feltColour += emissive;

    // ---------------------------------------------------------------- rail
    float railT = smoothstep(-aaHalf, aaHalf, dFelt);
    vec3 tableColour = feltColour;

    if (railT > 0.0) {
        // Cross-section: 0 at the outer edge of the table, 1 at the felt.
        float s = clamp(-dOut / RAIL, 0.0, 1.0);
        vec2 railNxy = gOut * (${num(RAIL_SLOPE)} * cos(PI * s));
        railNxy += (grain.rg * 2.0 - 1.0) * 0.09;

        // Width-guarded exactly as the well hairline is.
        //
        // PIN_WIDTH is a fixed 1.7 design units, and the stage fits by
        // contain fit — so on a 1280-wide window the pinstripe is 1.13 device
        // pixels and on a 960-wide window 0.85. Sub-pixel, following a rounded
        // corner, against a large albedo step to gold: it shimmers and breaks
        // up on exactly the laptop-half-window case where the game can least
        // afford to look cheap.
        float pinW = max(${num(PIN_WIDTH)}, aa * 1.3);
        float stripeD = (s - ${num(PIN_AT)}) * RAIL / pinW;
        float stripe = exp(-stripeD * stripeD);

        // The pinstripe is an inlaid flat band, not part of the cushion. Left
        // on the cushion its facet would face the light on the *lower* rail and
        // the line would be brightest on the wrong side of the table.
        railNxy = mix(railNxy, vec2(0.0), stripe);

        vec3 railColour = shade(
            vPos,
            normalize(vec3(railNxy, 1.0)),
            mix(RAIL_BASE, GOLD_DEEP, stripe * ${num(PIN_MIX)}),
            mix(${num(RAIL_SPEC)}, ${num(PIN_SPEC)}, stripe),
            mix(${num(RAIL_SPEC_POW)}, ${num(PIN_SPEC_POW)}, stripe),
            RAIL_SHEEN,
            0.55 + 0.45 * sin(PI * s),
            1.0,
            ${num(RAIL_GAIN)}
        );

        tableColour = mix(feltColour, railColour, railT);
    }

    // ---------------------------------------------------------------- beam
    // After the rail mix: the shaft is haze in front of the table, so it
    // crosses felt and rail alike rather than stopping at the cushion.
${shaftBlock(light)}
    // --------------------------------------------------------------- output
    vec3 srgb = pow(max(tableColour, 0.0), vec3(1.0 / 2.2));

    // One LSB of dither. A smooth gradient over near-black plum bands without it.
    srgb += (hash21(gl_FragCoord.xy) - 0.5) * (1.0 / 255.0);
    srgb = clamp(srgb, 0.0, 1.0);

    finalColor = vec4(srgb * tableMask, tableMask) * vColor;
}
`;
}

/* ------------------------------------------------------------------------ *
 * Per-well animation state. gsap tweens these plain objects; `sync` copies
 * them into the preallocated uniform arrays, so nothing allocates per frame.
 * ------------------------------------------------------------------------ */

interface WellAnim {
  hover: number;
  wild: number;
  pulse: number;
  tiltX: number;
  tiltY: number;
}

/* ------------------------------------------------------------------------ */

/**
 * The table surface. Satisfies the `createTable(): Container` contract while
 * exposing the interactive affordances the game drives it with.
 */
export class Table extends Container {
  readonly #mesh: Mesh<MeshGeometry, Shader>;
  readonly #geometry: MeshGeometry;
  readonly #shader: Shader;
  readonly #uniforms: UniformGroup<Record<string, UniformData>>;
  readonly #fabric: Texture;
  readonly #base: Graphics;

  readonly #state = new Float32Array(PILE_COUNT * 4);
  readonly #pulseColour = new Float32Array(PILE_COUNT * 3);
  readonly #tilt = new Float32Array(PILE_COUNT * 2);
  /** (r, g, b, amount) for the felt's streak tint. Linear space; see the shader. */
  readonly #streak = new Float32Array([1, 1, 1, 0]);

  readonly #impactConfig: ImpactConfig;
  /** Impact ripples, packed as (x, y, phase 0..1, strength). Empty when off. */
  readonly #ripple: Float32Array;
  /** Blanket multiplier on impact strength, folded in once at construction. */
  readonly #rippleScale: number;
  #rippleTicker: gsap.TickerCallback | null = null;

  readonly #anim: WellAnim[] = [];
  readonly #sync: Array<() => void> = [];
  readonly #wildTimelines: Array<gsap.core.Timeline | null> = [];
  readonly #pulseTimelines: Array<gsap.core.Timeline | null> = [];

  /** Proxy transforms for `Choreographer`. Inert until `pileWells()` is called. */
  readonly #wells: Container[] = [];
  /** Rest positions, flattened. `pilePosition` allocates; the sync runs hot. */
  readonly #wellHome = new Float32Array(PILE_COUNT * 2);
  #wellTicker: (() => void) | null = null;

  #destroyed = false;
  /**
   * Seconds since the table was built, wrapped.
   *
   * Drives the lamp breathing in the felt shader. Wrapped at a common multiple
   * of both breathing rates so the float never grows large enough to lose
   * precision in a long session, and so the wrap itself is seamless.
   */
  #time = 0;
  /**
   * Time scale for anything here driven by `gsap.ticker` rather than by a
   * timeline — the ripple and the lamp clock.
   *
   * A GSAP timeline time scale does not touch the ticker, so during a hit-stop
   * the cards would freeze while the felt kept rippling at full rate and the
   * two would visibly tear apart. `main.ts` forwards the choreographer's factor
   * here so the whole table stops together.
   */
  #timeScale = 1;
  #wildOn = false;

  constructor(options: TableOptions = {}) {
    super({ label: 'table' });

    // Decorative only — never intercept pointer input meant for the cards.
    this.eventMode = 'none';

    const impact = resolveImpact(options.impact);
    const light = resolveLight(options.light);
    this.#impactConfig = impact;
    this.#ripple = new Float32Array(impact.slots * 4);
    this.#rippleScale = prefersReducedMotion() ? impact.reducedMotionScale : 1;

    for (let i = 0; i < PILE_COUNT; i++) {
      this.#anim.push({ hover: 0, wild: 0, pulse: 0, tiltX: 0, tiltY: 0 });
      this.#sync.push(() => this.#syncWell(i));
      this.#wildTimelines.push(null);
      this.#pulseTimelines.push(null);
      writeLinear(this.#pulseColour, i * 3, palette.violetLight);

      const p = pilePosition(i);
      this.#wellHome[i * 2] = p.x;
      this.#wellHome[i * 2 + 1] = p.y;

      const well = new Container({ label: `pile-well-${i}`, x: p.x, y: p.y });
      well.eventMode = 'none';
      this.#wells.push(well);
    }

    // Flat fallback, so the table is never a hole if the custom GLSL program
    // cannot run (a WebGPU renderer skips GLSL-only mesh shaders outright).
    const inset = TABLE.inset;
    const rail = TABLE.railThickness;
    this.#base = new Graphics()
      .roundRect(inset, inset, DESIGN.width - inset * 2, DESIGN.height - inset * 2, TABLE.radius)
      .fill({ color: RAIL_BASE })
      .roundRect(
        inset + rail,
        inset + rail,
        DESIGN.width - (inset + rail) * 2,
        DESIGN.height - (inset + rail) * 2,
        RAIL_INNER_RADIUS,
      )
      .fill({ color: palette.felt });
    this.addChild(this.#base);

    this.#fabric = createFabricTexture();

    const x0 = inset;
    const y0 = inset;
    const x1 = DESIGN.width - inset;
    const y1 = DESIGN.height - inset;

    this.#geometry = new MeshGeometry({
      positions: new Float32Array([x0, y0, x1, y0, x1, y1, x0, y1]),
      uvs: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]),
      indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
    });

    const uniforms: Record<string, UniformData> = {
      uWellState: { value: this.#state, type: 'vec4<f32>', size: PILE_COUNT },
      uWellPulse: { value: this.#pulseColour, type: 'vec3<f32>', size: PILE_COUNT },
      uWellTilt: { value: this.#tilt, type: 'vec2<f32>', size: PILE_COUNT },
      uTime: { value: 0, type: 'f32' },
      uStreak: { value: this.#streak, type: 'vec4<f32>' },
    };

    // Declared only when the effect exists, so a table built with impacts
    // switched off carries neither the uniforms nor the code that reads them.
    if (impact.slots > 0) {
      uniforms.uRipple = { value: this.#ripple, type: 'vec4<f32>', size: impact.slots };
      uniforms.uRippleGate = { value: 0, type: 'f32' };
    }

    // Built here rather than left to `Shader.from` so the gate can be written
    // by name at runtime without going through an untyped resource accessor.
    this.#uniforms = new UniformGroup(uniforms);

    // The clock runs for the table's whole life: unlike the ripple and well
    // tickers, which mount only while something is animating, the lamp is
    // always on. One float write per frame against a mesh that is redrawn every
    // frame anyway.
    gsap.ticker.add(this.#advanceClock);

    this.#shader = Shader.from({
      gl: { vertex: VERTEX, fragment: buildFragment(impact, light), name: 'table-surface' },
      resources: {
        uFabric: this.#fabric.source,
        uFabricSampler: this.#fabric.source.style,
        tableUniforms: this.#uniforms,
      },
    });

    this.#mesh = new Mesh<MeshGeometry, Shader>({
      geometry: this.#geometry,
      shader: this.#shader,
      label: 'table-surface',
    });
    this.addChild(this.#mesh);
  }

  /** Copies one well's animated state into the uniform arrays. */
  #syncWell(i: number): void {
    const a = this.#anim[i];
    const s = i * 4;
    this.#state[s] = a.hover;
    this.#state[s + 1] = a.wild;
    this.#state[s + 2] = a.pulse;

    // Any displacement the Choreographer put on the proxy well rides along with
    // the API tilt, so a press or a rejection drags the glow with it 1:1.
    const w = this.#wells[i];
    const t = i * 2;
    this.#tilt[t] = a.tiltX + (w.x - this.#wellHome[t]) / WELL_PARALLAX;
    this.#tilt[t + 1] = a.tiltY + (w.y - this.#wellHome[t + 1]) / WELL_PARALLAX;
  }

  /** Syncs every well. Only mounted while something is driving the proxies. */
  #syncAllWells = (): void => {
    for (let i = 0; i < PILE_COUNT; i++) this.#syncWell(i);
  };

  #valid(i: number): boolean {
    return !this.#destroyed && Number.isInteger(i) && i >= 0 && i < PILE_COUNT;
  }

  /**
   * A card touching down at `x, y` in design units.
   *
   * Puts a fast radial crease into the felt's normal that travels outward and
   * settles inside `duration` (~200ms by default). `strength` is 0..1 and is
   * the only knob the caller needs: a card thrown onto a pile lands harder than
   * one placed on the stock.
   *
   * Cheap to call as often as the choreography lands cards. The slots are
   * preallocated, nothing here allocates, and a riffle that outruns the slot
   * count recycles the oldest ripple rather than dropping the newest — the most
   * recent impact is always the one worth showing.
   */
  impact(x: number, y: number, strength = 1): void {
    if (this.#destroyed || this.#impactConfig.slots === 0) return;
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(strength)) return;

    const scaled = clamp(strength, 0, 1) * this.#rippleScale;
    if (scaled <= 0) return;

    // Free slot if there is one, otherwise the ripple furthest through its
    // life — which is the oldest, without having to keep birth timestamps.
    const slots = this.#impactConfig.slots;
    let slot = 0;
    let oldest = -1;
    for (let i = 0; i < slots; i++) {
      if (this.#ripple[i * 4 + 3] === 0) {
        slot = i;
        break;
      }
      if (this.#ripple[i * 4 + 2] > oldest) {
        oldest = this.#ripple[i * 4 + 2];
        slot = i;
      }
    }

    const s = slot * 4;
    this.#ripple[s] = x;
    this.#ripple[s + 1] = y;
    this.#ripple[s + 2] = 0;
    this.#ripple[s + 3] = scaled;

    this.#uniforms.uniforms.uRippleGate = 1;
    if (!this.#rippleTicker) {
      this.#rippleTicker = this.#advanceRipples;
      gsap.ticker.add(this.#rippleTicker);
    }
  }

  /**
   * Ages every live ripple.
   *
   * Advanced by the frame delta rather than against a wall clock: gsap parks
   * its ticker when nothing is animating, and a ripple born against a stale
   * `ticker.time` starts life halfway through it or already dead.
   *
   * Mounted only while at least one ripple is alive and unmounted the frame the
   * last one dies, so a table with nothing landing on it does no per-frame work
   * at all — and neither does its shader, which is gated on the same flag.
   */
  #advanceRipples = (_time: number, deltaMs: number): void => {
    const slots = this.#impactConfig.slots;
    // Guarded rather than trusted: a non-positive or non-finite delta would
    // otherwise poison a phase, and a NaN in the uniform array takes the whole
    // felt with it — the ripple would never age out to clear it.
    const step =
      deltaMs > 0 ? (deltaMs * this.#timeScale) / (this.#impactConfig.duration * 1000) : 0;
    let alive = 0;

    for (let i = 0; i < slots; i++) {
      const s = i * 4;
      if (this.#ripple[s + 3] === 0) continue;

      const phase = this.#ripple[s + 2] + step;
      if (phase >= 1) {
        this.#ripple[s + 2] = 1;
        this.#ripple[s + 3] = 0;
        continue;
      }

      this.#ripple[s + 2] = phase;
      alive++;
    }

    if (alive > 0) return;

    this.#uniforms.uniforms.uRippleGate = 0;
    this.#stopRipples();
  };

  #stopRipples(): void {
    if (!this.#rippleTicker) return;
    gsap.ticker.remove(this.#rippleTicker);
    this.#rippleTicker = null;
  }

  /**
   * Hover affordance. Choosing a pile is the player's only decision, so the
   * attack is fast and the release is slower — it should feel like the table
   * is already responding before the pointer has settled.
   */
  highlightPile(i: number, on: boolean): void {
    if (!this.#valid(i)) return;

    gsap.to(this.#anim[i], {
      hover: on ? 1 : 0,
      duration: on ? HOVER_IN : HOVER_OUT,
      ease: on ? 'power3.out' : 'power2.inOut',
      overwrite: 'auto',
      onUpdate: this.#sync[i],
    });
  }

  /** A brief wash of the outcome's token colour when a pile resolves. */
  pulsePile(i: number, kind: PulseKind): void {
    if (!this.#valid(i)) return;

    writeLinear(this.#pulseColour, i * 3, PULSE_COLOUR[kind]);

    const anim = this.#anim[i];
    const sync = this.#sync[i];
    const fade = kind === 'bust' ? motion.bust : motion.clear;

    this.#pulseTimelines[i]?.kill();
    gsap.killTweensOf(anim, 'pulse');
    anim.pulse = 0;

    this.#pulseTimelines[i] = gsap
      .timeline({ onComplete: () => (this.#pulseTimelines[i] = null) })
      .to(anim, { pulse: 1, duration: motion.ui * 0.42, ease: 'power3.out', onUpdate: sync })
      .to(anim, { pulse: 0, duration: fade, ease: 'power2.out', onUpdate: sync });
  }

  /**
   * While a wild is the active card every well breathes violet, staggered so
   * the four read as one wave: "this card clears any of these".
   */
  setWildAlert(on: boolean): void {
    // Idempotent: the game re-asserts this on every snapshot, and restarting
    // the timelines each time would reset the wave to its first frame.
    if (this.#destroyed || on === this.#wildOn) return;
    this.#wildOn = on;

    for (let i = 0; i < PILE_COUNT; i++) {
      this.#wildTimelines[i]?.kill();
      this.#wildTimelines[i] = null;

      const anim = this.#anim[i];
      const sync = this.#sync[i];
      gsap.killTweensOf(anim, 'wild');

      if (!on) {
        gsap.to(anim, { wild: 0, duration: motion.ui, ease: 'power2.out', onUpdate: sync });
        continue;
      }

      this.#wildTimelines[i] = gsap
        .timeline({ delay: i * WILD_STAGGER })
        .to(anim, { wild: 1, duration: WILD_RISE, ease: 'power2.out', onUpdate: sync })
        .to(anim, {
          wild: 0.3,
          duration: WILD_BREATH,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1,
          onUpdate: sync,
        });
    }
  }

  /**
   * Subtle parallax lean of a well's contents. `dx`/`dy` are unit-ish values,
   * clamped to ±1; anything past a light lean starts fighting the lighting.
   */
  setPileTilt(i: number, dx: number, dy: number): void {
    if (!this.#valid(i)) return;

    gsap.to(this.#anim[i], {
      tiltX: Math.max(-1, Math.min(1, dx)),
      tiltY: Math.max(-1, Math.min(1, dy)),
      duration: motion.ui,
      ease: 'power3.out',
      overwrite: 'auto',
      onUpdate: this.#sync[i],
    });
  }

  /**
   * Proxy transforms for `Choreographer`'s `pileWells` option, one per pile,
   * parked at `pilePosition(i)`. The wells themselves live in the shader and
   * cannot be scene-graph children, so these stand in: whatever displacement
   * the choreographer puts on them drags the well's glow along with it, which
   * is what makes a press or a rejection land on the felt and not just on the
   * card. Hover brightness stays with `highlightPile` — motion moves things,
   * the table lights them.
   *
   * Calling this mounts a small per-frame sync (no allocation). Don't call it
   * if nothing is going to drive them.
   */
  pileWells(): readonly Container[] {
    if (!this.#wellTicker && !this.#destroyed) {
      this.#wellTicker = this.#syncAllWells;
      if (!this.#idle) gsap.ticker.add(this.#wellTicker);
    }
    return this.#wells;
  }

  /**
   * The well footprint in design units, so whoever wires up pointer input does
   * not have to re-derive the recess geometry.
   */
  pileBounds(i: number): Rectangle {
    const clamped = Math.max(0, Math.min(PILE_COUNT - 1, i | 0));
    const p = pilePosition(clamped);
    return new Rectangle(
      p.x - WELL_HALF_X,
      p.y - WELL_HALF_Y,
      WELL_HALF_X * 2,
      WELL_HALF_Y * 2,
    );
  }

  /**
   * Advances the shader clock.
   *
   * Same delta-driven approach as `#advanceRipples`, and guarded the same way:
   * a non-finite delta would put a NaN in `uTime`, which would take the entire
   * felt with it and never recover.
   */
  #advanceClock = (_time: number, deltaMs: number): void => {
    if (this.#destroyed) return;
    const step = deltaMs > 0 && deltaMs < 500 ? (deltaMs / 1000) * this.#timeScale : 0;
    // 2*PI / gcd(0.62, 1.47) — both sines complete whole cycles, so the wrap is
    // continuous rather than a jump.
    this.#time = (this.#time + step) % LAMP_WRAP;
    this.#uniforms.uniforms.uTime = this.#time;
  };

  /**
   * Lean the felt toward a live streak's colour.
   *
   * The quietest of the four streak channels, and the only diegetic one: the
   * rim is the frame around the scene and the callout is over it, but this is
   * the room itself knowing that a run is alive. Driven every frame from
   * `StreakAura`'s own eased level, so the felt, the rim and the letterbox spill
   * are always burning the identical hue at the identical strength.
   *
   * Written straight into the uniform array with no easing of its own — the
   * value arriving here has already been eased twice, once by the aura's level
   * ramp and once by its fade.
   *
   * @param colour - 0xRRGGBB, from `@/vfx/streakLadder`.
   * @param amount - 0..1. Zero costs the shader a coherent branch and nothing else.
   */
  setStreakTint(colour: number, amount: number): void {
    if (this.#destroyed) return;
    const next = Number.isFinite(amount) ? clamp(amount, 0, 1) : 0;
    // Nothing to do, and nothing to dirty, on the great majority of frames.
    if (next === 0 && this.#streak[3] === 0) return;
    writeLinear(this.#streak, 0, colour);
    this.#streak[3] = next;
  }

  /**
   * Scale the ticker-driven effects. 1 is normal; a hit-stop passes its factor.
   *
   * @param factor - 0..1.
   */
  setTimeScale(factor: number): void {
    this.#timeScale = Number.isFinite(factor) ? Math.min(Math.max(factor, 0), 1) : 1;
  }

  /**
   * Park the lamp clock while the table is frozen under a shell screen.
   *
   * The write itself is nothing; what it costs is gsap's ticker. gsap sleeps
   * on its own once no tween is live — unless something is listening, and
   * this clock always was, so the ticker ran every frame under the home for
   * a uniform on a mesh nobody was redrawing. Off the ticker it sleeps; a
   * deal puts the clock back and any tween wakes the ticker regardless.
   */
  setIdle(idle: boolean): void {
    if (this.#destroyed || idle === this.#idle) return;
    this.#idle = idle;
    if (idle) {
      gsap.ticker.remove(this.#advanceClock);
      // The well sync is mounted for the table's life once anything asks for
      // the wells (input does, at boot); it is the other listener that kept
      // the ticker awake. Parked, not unmounted: `pileWells` still reports
      // it mounted, and the wake below puts it back.
      if (this.#wellTicker) gsap.ticker.remove(this.#wellTicker);
    } else {
      gsap.ticker.add(this.#advanceClock);
      if (this.#wellTicker) gsap.ticker.add(this.#wellTicker);
    }
  }

  #idle = false;

  override destroy(options?: DestroyOptions): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    gsap.ticker.remove(this.#advanceClock);

    if (this.#wellTicker) {
      gsap.ticker.remove(this.#wellTicker);
      this.#wellTicker = null;
    }
    this.#stopRipples();

    for (let i = 0; i < PILE_COUNT; i++) {
      this.#wildTimelines[i]?.kill();
      this.#wildTimelines[i] = null;
      this.#pulseTimelines[i]?.kill();
      this.#pulseTimelines[i] = null;
      gsap.killTweensOf(this.#anim[i]);
      gsap.killTweensOf(this.#wells[i]);
      this.#wells[i].destroy();
    }

    // Detach first: a caller passing `{ children: true }` must not get a second
    // pass over things this method has already torn down.
    this.removeChildren();

    this.#mesh.destroy();
    this.#geometry.destroy();
    // `false`: GlProgram instances are cached globally by source, so the
    // program may outlive this table and must not be pulled from under it.
    this.#shader.destroy(false);
    this.#fabric.destroy(true);
    this.#base.destroy();

    super.destroy(options);
  }
}

/**
 * Builds the table. Add it under everything else in the scene graph: it is the
 * surface the cards sit on, and it never takes pointer input.
 *
 * `options` tunes the two effects that are not part of the base surface — the
 * felt's impact response and the visible light. Both are baked into the shader
 * source at construction, so `{ impact: { slots: 0 } }` or
 * `{ light: { pool: 0, poolHaze: 0, shaft: 0 } }` removes the work entirely
 * rather than leaving it running at zero: the GLSL is never emitted, the
 * uniforms are never declared, and `impact()` becomes a no-op.
 */
export function createTable(options?: TableOptions): Table {
  // The lamp aim, the well extent and the pool size are baked into this
  // table's shader as literals, so they have to be rederived from the current
  // orientation before the shader is built. See `syncGeometry`.
  syncGeometry();
  return new Table(options);
}
